import { useSnackbar } from "notistack";
import { Button, Card, CardTitle, Heading, Separator, SwitchInput } from "components/elements";
import { trpc } from "lib/trpc";

// Push notifications on web are temporarily hidden — flip back to `true` when
// we're ready to expose them again.
const SHOW_PUSH_SECTION = false;

interface BucketSwitchProps {
    title: string;
    subtitle: string;
    name: string;
    value: boolean;
    onChange: (checked: boolean) => void;
}

function BucketSwitch({ title, subtitle, name, value, onChange }: BucketSwitchProps) {
    return (
        <div className="flex items-center justify-between rounded-lg bg-gray-50 p-3">
            <div>
                <h4 className="text-sm font-medium text-gray-900">{title}</h4>
                <p className="text-xs text-gray-600">{subtitle}</p>
            </div>
            <SwitchInput name={name} label="" value={value} onChange={onChange} />
        </div>
    );
}

export const SettingsNotifications = () => {
    const { enqueueSnackbar } = useSnackbar();
    const utils = trpc.useUtils();

    // Current user — drives bucket visibility (isSocialEnabled / isPrivateEnabled).
    const userQuery = trpc.users.me.useQuery();
    const showPublicBucket = !!userQuery.data?.isSocialEnabled;
    const showPrivateBucket = !!userQuery.data?.isPrivateEnabled;
    // Document-upload alerts only ever fan out to Compass Users / Super Users, so
    // the toggles are meaningless for other roles — show them to Compass only (PRTL-1322).
    const isCompassUser =
        userQuery.data?.role === "COMPASS_USER" || userQuery.data?.role === "COMPASS_SUPERUSER";

    // Notification preferences
    const notificationPrefsQuery = trpc.notificationPreferences.getPreferences.useQuery();
    const updateNotificationPrefs = trpc.notificationPreferences.updatePreferences.useMutation({
        onMutate: async (newPrefs) => {
            // Cancel outgoing refetches so they don't overwrite our optimistic update
            await utils.notificationPreferences.getPreferences.cancel();
            const previous = utils.notificationPreferences.getPreferences.getData();
            // Optimistically update the cache so toggles respond instantly
            if (previous) {
                utils.notificationPreferences.getPreferences.setData(undefined, {
                    ...previous,
                    ...newPrefs,
                });
            }
            return { previous };
        },
        onError: (_error, _newPrefs, context) => {
            // Revert to previous data on error
            if (context?.previous) {
                utils.notificationPreferences.getPreferences.setData(undefined, context.previous);
            }
            enqueueSnackbar("Error updating notification preferences.", { variant: "error" });
        },
        onSettled: () => {
            // Refetch to ensure server state is in sync
            utils.notificationPreferences.getPreferences.invalidate();
        },
    });

    // Device management
    const devicesQuery = trpc.devices.getDevices.useQuery();
    const unregisterDevice = trpc.devices.unregisterDeviceToken.useMutation({
        onSuccess: () => {
            enqueueSnackbar("Device unregistered successfully.", { variant: "success" });
            devicesQuery.refetch();
        },
        onError: (error) => {
            console.error("Error unregistering device:", error);
            enqueueSnackbar("Error unregistering device.", { variant: "error" });
        },
    });

    const prefs = notificationPrefsQuery.data;
    const isLoading = notificationPrefsQuery.isLoading;

    return (
        <Card>
            <CardTitle>Notification Settings</CardTitle>

            {/* Push Notifications Section — temporarily hidden on web. */}
            {SHOW_PUSH_SECTION && (
                <div className="mb-6">
                    <Heading>Push Notifications</Heading>
                    <p className="mb-4 text-sm text-gray-600">
                        Receive notifications on your mobile devices when you get messages,
                        comments, friend requests, and discussion updates.
                    </p>

                    {isLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* Master Push Toggle */}
                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="font-medium text-gray-900">
                                            Enable Push Notifications
                                        </h3>
                                        <p className="text-sm text-gray-600">
                                            Master toggle for all push notifications
                                        </p>
                                    </div>
                                    <SwitchInput
                                        name="enablePushNotifications"
                                        label=""
                                        value={prefs?.enablePushNotifications ?? true}
                                        onChange={(checked) => {
                                            updateNotificationPrefs.mutate({
                                                enablePushNotifications: checked,
                                            });
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Individual Push Notification Types */}
                            {prefs?.enablePushNotifications && (
                                <div className="ml-4 space-y-3 border-l-2 border-gray-200 pl-4">
                                    <BucketSwitch
                                        title="Messages"
                                        subtitle="Direct message notifications"
                                        name="enableMessages"
                                        value={prefs?.enableMessages ?? true}
                                        onChange={(checked) =>
                                            updateNotificationPrefs.mutate({
                                                enableMessages: checked,
                                            })
                                        }
                                    />
                                    <BucketSwitch
                                        title="Social Comments"
                                        subtitle="Comments on your posts"
                                        name="enableSocialComments"
                                        value={prefs?.enableSocialComments ?? true}
                                        onChange={(checked) =>
                                            updateNotificationPrefs.mutate({
                                                enableSocialComments: checked,
                                            })
                                        }
                                    />
                                    <BucketSwitch
                                        title="Friend Requests"
                                        subtitle="New friend requests"
                                        name="enableFriendRequests"
                                        value={prefs?.enableFriendRequests ?? true}
                                        onChange={(checked) =>
                                            updateNotificationPrefs.mutate({
                                                enableFriendRequests: checked,
                                            })
                                        }
                                    />
                                    <BucketSwitch
                                        title="Discussions"
                                        subtitle="Discussion activity"
                                        name="enableDiscussions"
                                        value={prefs?.enableDiscussions ?? true}
                                        onChange={(checked) =>
                                            updateNotificationPrefs.mutate({
                                                enableDiscussions: checked,
                                            })
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {SHOW_PUSH_SECTION && (
                <>
                    <Separator />
                    <div className="h-4" />
                </>
            )}

            {/* Email Notifications — Social bucket */}
            {showPublicBucket && (
                <div className="mb-6">
                    <Heading>Email Notifications — Social</Heading>
                    <p className="mb-4 text-sm text-gray-600">
                        Emails about social activity, AI digests, RSS feeds, and blog posts.
                    </p>

                    {isLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="font-medium text-gray-900">
                                            Enable Social Email Notifications
                                        </h3>
                                        <p className="text-sm text-gray-600">
                                            Master toggle for the social portal
                                        </p>
                                    </div>
                                    <SwitchInput
                                        name="publicNotificationsEnabled"
                                        label=""
                                        value={prefs?.publicNotificationsEnabled ?? false}
                                        onChange={(checked) => {
                                            updateNotificationPrefs.mutate({
                                                publicNotificationsEnabled: checked,
                                            });
                                        }}
                                    />
                                </div>
                            </div>

                            {prefs?.publicNotificationsEnabled && (
                                <div className="ml-4 space-y-3 border-l-2 border-gray-200 pl-4">
                                    <BucketSwitch
                                        title="Messages"
                                        subtitle="Direct message notifications"
                                        name="enableEmailMessages"
                                        value={prefs?.enableEmailMessages ?? false}
                                        onChange={(checked) =>
                                            updateNotificationPrefs.mutate({
                                                enableEmailMessages: checked,
                                            })
                                        }
                                    />
                                    <BucketSwitch
                                        title="Social Comments"
                                        subtitle="Comments on your posts"
                                        name="enableEmailSocialComments"
                                        value={prefs?.enableEmailSocialComments ?? false}
                                        onChange={(checked) =>
                                            updateNotificationPrefs.mutate({
                                                enableEmailSocialComments: checked,
                                            })
                                        }
                                    />
                                    <BucketSwitch
                                        title="Friend Requests"
                                        subtitle="New friend requests"
                                        name="enableEmailFriendRequests"
                                        value={prefs?.enableEmailFriendRequests ?? false}
                                        onChange={(checked) =>
                                            updateNotificationPrefs.mutate({
                                                enableEmailFriendRequests: checked,
                                            })
                                        }
                                    />
                                    <BucketSwitch
                                        title="Daily AI Digest"
                                        subtitle="AI-generated news summaries"
                                        name="enableEmailDigestNotifications"
                                        value={prefs?.enableEmailDigestNotifications ?? false}
                                        onChange={(checked) =>
                                            updateNotificationPrefs.mutate({
                                                enableEmailDigestNotifications: checked,
                                            })
                                        }
                                    />
                                    <BucketSwitch
                                        title="Weekly Feed Summary"
                                        subtitle="RSS feed roundup"
                                        name="enableEmailFeedNotifications"
                                        value={prefs?.enableEmailFeedNotifications ?? false}
                                        onChange={(checked) =>
                                            updateNotificationPrefs.mutate({
                                                enableEmailFeedNotifications: checked,
                                            })
                                        }
                                    />
                                    <BucketSwitch
                                        title="New Blog Articles"
                                        subtitle="Instant notifications for new posts"
                                        name="enableEmailArticleNotifications"
                                        value={prefs?.enableEmailArticleNotifications ?? false}
                                        onChange={(checked) =>
                                            updateNotificationPrefs.mutate({
                                                enableEmailArticleNotifications: checked,
                                            })
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {showPublicBucket && showPrivateBucket && (
                <>
                    <Separator />
                    <div className="h-4" />
                </>
            )}

            {/* Email Notifications — Private bucket */}
            {showPrivateBucket && (
                <div className="mb-6">
                    <Heading>Email Notifications — Private</Heading>
                    <p className="mb-4 text-sm text-gray-600">
                        Emails about discussions, client file reports, and visualizations.
                    </p>

                    {isLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="font-medium text-gray-900">
                                            Enable Private Email Notifications
                                        </h3>
                                        <p className="text-sm text-gray-600">
                                            Master toggle for the private portal
                                        </p>
                                    </div>
                                    <SwitchInput
                                        name="privateNotificationsEnabled"
                                        label=""
                                        value={prefs?.privateNotificationsEnabled ?? false}
                                        onChange={(checked) => {
                                            updateNotificationPrefs.mutate({
                                                privateNotificationsEnabled: checked,
                                            });
                                        }}
                                    />
                                </div>
                            </div>

                            {prefs?.privateNotificationsEnabled && (
                                <div className="ml-4 space-y-3 border-l-2 border-gray-200 pl-4">
                                    <BucketSwitch
                                        title="Discussions"
                                        subtitle="Discussion activity"
                                        name="enableEmailDiscussions"
                                        value={prefs?.enableEmailDiscussions ?? false}
                                        onChange={(checked) =>
                                            updateNotificationPrefs.mutate({
                                                enableEmailDiscussions: checked,
                                            })
                                        }
                                    />
                                    <BucketSwitch
                                        title="Client File Activity"
                                        subtitle="New client file reports and comments on existing ones"
                                        name="enableEmailClientFileReportActivity"
                                        value={prefs?.enableEmailClientFileReportActivity ?? false}
                                        onChange={(checked) =>
                                            updateNotificationPrefs.mutate({
                                                enableEmailClientFileReportActivity: checked,
                                            })
                                        }
                                    />
                                    <BucketSwitch
                                        title="Visualization Activity"
                                        subtitle="New visualizations and comments on existing ones"
                                        name="enableEmailVisualizationActivity"
                                        value={prefs?.enableEmailVisualizationActivity ?? false}
                                        onChange={(checked) =>
                                            updateNotificationPrefs.mutate({
                                                enableEmailVisualizationActivity: checked,
                                            })
                                        }
                                    />
                                    {isCompassUser && (
                                        <>
                                            <BucketSwitch
                                                title="Compass Document Uploads"
                                                subtitle="When a Compass user or super user uploads a document"
                                                name="enableEmailCompassDocumentUploads"
                                                value={
                                                    prefs?.enableEmailCompassDocumentUploads ??
                                                    false
                                                }
                                                onChange={(checked) =>
                                                    updateNotificationPrefs.mutate({
                                                        enableEmailCompassDocumentUploads: checked,
                                                    })
                                                }
                                            />
                                            <BucketSwitch
                                                title="Non-Compass Document Uploads"
                                                subtitle="When a client (non-Compass user) uploads a document"
                                                name="enableEmailDocumentUploads"
                                                value={prefs?.enableEmailDocumentUploads ?? false}
                                                onChange={(checked) =>
                                                    updateNotificationPrefs.mutate({
                                                        enableEmailDocumentUploads: checked,
                                                    })
                                                }
                                            />
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {SHOW_PUSH_SECTION && (
                <>
                    <Separator />
                    <div className="h-4" />
                </>
            )}

            {/* Content Digest Push (email moved into Social Email bucket) */}
            {SHOW_PUSH_SECTION && (
                <div className="mb-6">
                    <Heading>Content Digest Push Notifications</Heading>
                    <p className="mb-4 text-sm text-gray-600">
                        Push notifications about AI summaries, RSS feeds, and new blog posts.
                    </p>

                    {isLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="rounded-lg border border-gray-200 p-4">
                                <div className="mb-3 flex items-center justify-between">
                                    <div>
                                        <h3 className="font-medium text-gray-900">
                                            Daily AI Digest
                                        </h3>
                                        <p className="text-sm text-gray-600">
                                            AI-generated news summaries (8 AM ET)
                                        </p>
                                    </div>
                                    <SwitchInput
                                        name="enableDigestNotifications"
                                        label=""
                                        value={prefs?.enableDigestNotifications ?? true}
                                        onChange={(checked) => {
                                            updateNotificationPrefs.mutate({
                                                enableDigestNotifications: checked,
                                            });
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="rounded-lg border border-gray-200 p-4">
                                <div className="mb-3 flex items-center justify-between">
                                    <div>
                                        <h3 className="font-medium text-gray-900">
                                            Weekly Feed Summary
                                        </h3>
                                        <p className="text-sm text-gray-600">
                                            RSS feed roundup (Mondays at 8 AM ET)
                                        </p>
                                    </div>
                                    <SwitchInput
                                        name="enableFeedNotifications"
                                        label=""
                                        value={prefs?.enableFeedNotifications ?? true}
                                        onChange={(checked) => {
                                            updateNotificationPrefs.mutate({
                                                enableFeedNotifications: checked,
                                            });
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="rounded-lg border border-gray-200 p-4">
                                <div className="mb-3 flex items-center justify-between">
                                    <div>
                                        <h3 className="font-medium text-gray-900">
                                            New Blog Articles
                                        </h3>
                                        <p className="text-sm text-gray-600">
                                            Instant notifications for new posts
                                        </p>
                                    </div>
                                    <SwitchInput
                                        name="enableArticleNotifications"
                                        label=""
                                        value={prefs?.enableArticleNotifications ?? true}
                                        onChange={(checked) => {
                                            updateNotificationPrefs.mutate({
                                                enableArticleNotifications: checked,
                                            });
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {SHOW_PUSH_SECTION && (
                <>
                    <Separator />
                    <div className="h-4" />
                </>
            )}

            {/* Registered Devices Section */}
            {SHOW_PUSH_SECTION && (
                <>
                    <Heading>Registered Devices</Heading>
                    {devicesQuery.isLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
                        </div>
                    ) : devicesQuery.data && devicesQuery.data.length > 0 ? (
                        <div className="space-y-3">
                            <p className="mb-4 text-sm text-gray-600">
                                These devices are registered to receive push notifications.
                            </p>
                            {devicesQuery.data.map((device) => (
                                <div
                                    key={device.id}
                                    className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4"
                                >
                                    <div className="flex items-center space-x-3">
                                        {device.platform === "ios" ? (
                                            <svg
                                                className="h-8 w-8 text-gray-700"
                                                fill="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                                            </svg>
                                        ) : (
                                            <svg
                                                className="h-8 w-8 text-gray-700"
                                                fill="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24a11.43 11.43 0 0 0-8.94 0L5.65 5.67c-.19-.28-.54-.37-.83-.22-.3.16-.42.54-.26.85l1.84 3.18C4.8 11.16 3.5 13.84 3.5 16.5h17c0-2.66-1.3-5.34-2.9-7.02zM7 14.5c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm10 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z" />
                                            </svg>
                                        )}
                                        <div>
                                            <h3 className="font-medium text-gray-900">
                                                {device.platform === "ios"
                                                    ? "iOS Device"
                                                    : "Android Device"}
                                            </h3>
                                            <p className="text-xs text-gray-600">
                                                Last active:{" "}
                                                {new Date(device.updatedAt).toLocaleDateString(
                                                    undefined,
                                                    {
                                                        year: "numeric",
                                                        month: "short",
                                                        day: "numeric",
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    },
                                                )}
                                            </p>
                                            <p className="font-mono text-xs text-gray-500">
                                                {device.token.substring(0, 20)}...
                                            </p>
                                        </div>
                                    </div>
                                    <Button
                                        size="smaller"
                                        variant="primary"
                                        color="red"
                                        onClick={() =>
                                            unregisterDevice.mutate({ token: device.token })
                                        }
                                        disabled={unregisterDevice.isPending}
                                    >
                                        Remove
                                    </Button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center">
                            <svg
                                className="mx-auto mb-3 h-12 w-12 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                                />
                            </svg>
                            <p className="text-sm text-gray-600">
                                No devices registered for push notifications
                            </p>
                            <p className="mt-2 text-xs text-gray-500">
                                Devices will appear here when push notifications are enabled on
                                mobile
                            </p>
                        </div>
                    )}
                    <div className="h-8" />
                </>
            )}
        </Card>
    );
};
